export const SOURCE_KEYS = {
  GLOBAL: 'global',
  ACTIVE_WATCHLIST: 'active_watchlist',
  OPEN_TRADES: 'open_trades',
  MANUAL_MODAL: 'manual_modal',
};

// Matches backend GlobalTokenResolver DEFAULT_GLOBAL_TOKENS (Nifty 50, Sensex).
export const DEFAULT_GLOBAL_TOKENS = Object.freeze([256265, 265]);

export const REPLAY_ORDER = [
  SOURCE_KEYS.GLOBAL,
  SOURCE_KEYS.ACTIVE_WATCHLIST,
  SOURCE_KEYS.OPEN_TRADES,
  SOURCE_KEYS.MANUAL_MODAL,
];

export const parsePositiveSafeInteger = (value) => {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }

  if (typeof value === 'string' && /^[1-9]\d*$/.test(value.trim())) {
    const parsed = Number(value.trim());
    return Number.isSafeInteger(parsed) ? parsed : null;
  }

  return null;
};

const clonePayloadValue = (value) => (Array.isArray(value) ? [...value] : value);

const clonePayload = (payload) => Object.fromEntries(
  Object.entries(payload).map(([key, value]) => [key, clonePayloadValue(value)])
);

const cloneTokens = (tokens) => (Array.isArray(tokens) ? [...tokens] : []);

export const createDesiredSubscriptionState = () => new Map();

export const snapshotDesiredSources = (state) =>
  REPLAY_ORDER.map((source) => state.get(source)).filter(Boolean).map((entry) => ({
    source: entry.source,
    payload: clonePayload(entry.payload),
  }));

export const getReplayPayloads = (state) =>
  snapshotDesiredSources(state).map((entry) => entry.payload);

export const normalizeGlobalTokens = (tokens) => {
  if (!Array.isArray(tokens) || tokens.length === 0) {
    return [...DEFAULT_GLOBAL_TOKENS];
  }
  const normalized = [];
  const seen = new Set();
  for (const value of tokens) {
    const token = parsePositiveSafeInteger(value);
    if (!token || seen.has(token)) continue;
    seen.add(token);
    normalized.push(token);
  }
  return normalized.length ? normalized : [...DEFAULT_GLOBAL_TOKENS];
};

export const setGlobalDesired = (state, tokens = []) => {
  // Always persist explicit tokens so tick cleanup keeps Nifty/Sensex after
  // later watchlist subscription_update messages. Empty input means defaults.
  const clonedTokens = normalizeGlobalTokens(tokens);
  const payload = { action: 'global', tokens: clonedTokens };
  state.set(SOURCE_KEYS.GLOBAL, { source: SOURCE_KEYS.GLOBAL, payload });
  return payload;
};

export const getDesiredGlobalTokens = (state) => {
  const payloadTokens = state?.get?.(SOURCE_KEYS.GLOBAL)?.payload?.tokens;
  return normalizeGlobalTokens(payloadTokens);
};

export const setActiveWatchlistDesired = (state, watchlistId) => {
  const id = parsePositiveSafeInteger(watchlistId);
  if (!id) return null;

  const payload = { action: 'switch_watchlist', watchlistId: id };
  state.set(SOURCE_KEYS.ACTIVE_WATCHLIST, {
    source: SOURCE_KEYS.ACTIVE_WATCHLIST,
    watchlistId: id,
    payload,
  });
  return payload;
};

export const clearActiveWatchlistDesired = (state, watchlistId) => {
  const current = state.get(SOURCE_KEYS.ACTIVE_WATCHLIST);
  if (!current) return null;

  const id = watchlistId === undefined || watchlistId === null
    ? current.watchlistId
    : parsePositiveSafeInteger(watchlistId);

  if (!id || id !== current.watchlistId) {
    return null;
  }

  state.delete(SOURCE_KEYS.ACTIVE_WATCHLIST);
  return { action: 'unsubscribe', watchlistId: current.watchlistId };
};

export const enableOpenTradesDesired = (state) => {
  const payload = { action: 'subscribe_open_trades' };
  state.set(SOURCE_KEYS.OPEN_TRADES, { source: SOURCE_KEYS.OPEN_TRADES, payload });
  return payload;
};

export const disableOpenTradesDesired = (state) => {
  if (!state.has(SOURCE_KEYS.OPEN_TRADES)) return null;
  state.delete(SOURCE_KEYS.OPEN_TRADES);
  return { action: 'unsubscribe_open_trades' };
};

export const setManualModalDesired = (state, tokens = []) => {
  const payload = { action: 'subscribe_open_trades_all', token: cloneTokens(tokens) };
  state.set(SOURCE_KEYS.MANUAL_MODAL, { source: SOURCE_KEYS.MANUAL_MODAL, payload });
  return payload;
};

export const clearManualModalDesired = (state) => {
  if (!state.has(SOURCE_KEYS.MANUAL_MODAL)) return null;
  state.delete(SOURCE_KEYS.MANUAL_MODAL);
  return { action: 'subscribe_open_trades_all', token: [] };
};

export const forceClearManualModalDesired = (state) => {
  state.delete(SOURCE_KEYS.MANUAL_MODAL);
  return { action: 'subscribe_open_trades_all', token: [] };
};

export const applyGenericSubscribeAction = (state, action, tokens, watchlistId) => {
  switch (action) {
    case 'global':
      return { payload: setGlobalDesired(state, tokens), persist: true };
    case 'subscribe':
    case 'switch_watchlist':
      return { payload: setActiveWatchlistDesired(state, watchlistId), persist: true };
    case 'unsubscribe':
      return { payload: clearActiveWatchlistDesired(state, watchlistId), persist: false };
    case 'subscribe_open_trades':
      return { payload: enableOpenTradesDesired(state), persist: true };
    case 'unsubscribe_open_trades':
      return { payload: disableOpenTradesDesired(state), persist: false };
    case 'subscribe_open_trades_all':
      if (Array.isArray(tokens) && tokens.length === 0) {
        return { payload: forceClearManualModalDesired(state), persist: false };
      }
      return { payload: setManualModalDesired(state, tokens), persist: true };
    case 'user_logout':
    case 'ping':
      return { payload: { action }, persist: false };
    default:
      return { payload: null, persist: false };
  }
};
